/**
 * svgDiff.js — path-level diff between two SVG strings.
 *
 * Given two SVG documents, we parse them, walk the DOM, and produce two
 * output SVGs (or one merged overlay) where:
 *   - Elements present in `next` but not in `prev` are ADDITIONS   → green
 *   - Elements present in `prev` but not in `next` are REMOVALS    → red
 *   - Elements present in both are UNCHANGED                       → muted grey
 *
 * The "same element" check uses a canonical signature built from the
 * tag name + the shape-defining attributes (d, points, cx/cy/r, x/y/w/h,
 * x1/y1/x2/y2, and — for <text> — the text content). Style attributes
 * (fill, stroke, stroke-width) are intentionally EXCLUDED so a
 * re-coloured element still counts as unchanged. This matches how an
 * architect thinks about drawing diffs: "did the geometry move?".
 */

const SHAPE_TAGS = new Set([
  'path', 'rect', 'circle', 'line', 'polyline', 'polygon', 'ellipse', 'text',
]);

// Attributes that DEFINE an element's identity (shape/geometry/label).
const SIG_ATTRS = [
  'd', 'points', 'x', 'y', 'width', 'height',
  'cx', 'cy', 'r', 'rx', 'ry',
  'x1', 'y1', 'x2', 'y2',
];

const OVERLAY_STYLES = {
  added:   { stroke: '#10B981', fill: 'rgba(16,185,129,0.18)',  strokeOpacity: 1 },
  removed: { stroke: '#EF4444', fill: 'rgba(239,68,68,0.18)',   strokeOpacity: 1 },
  unchanged: { stroke: '#94A3B8', fill: 'none',                 strokeOpacity: 0.45 },
};

const parseSvg = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    if (!svg) return null;
    // Verify no XML parse error injected a <parsererror>.
    if (doc.querySelector('parsererror')) return null;
    return svg;
  } catch {
    return null;
  }
};

// Round floats so tiny drift (145.001 vs 145.002) doesn't count as diff.
const round = (v, dp = 1) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n.toFixed(dp) : v;
};

const normalizeAttr = (name, value) => {
  if (value == null) return '';
  if (name === 'd' || name === 'points') {
    // Collapse whitespace + round numeric tokens.
    return value.trim().replace(/[\s,]+/g, ' ').replace(/-?\d+\.\d+/g, (m) => round(m));
  }
  if (['x', 'y', 'width', 'height', 'cx', 'cy', 'r', 'rx', 'ry',
       'x1', 'y1', 'x2', 'y2'].includes(name)) {
    return round(value);
  }
  return value;
};

const signatureOf = (el) => {
  const tag = el.tagName.toLowerCase();
  const attrs = SIG_ATTRS.map((a) => {
    const v = el.getAttribute(a);
    return v == null ? '' : `${a}=${normalizeAttr(a, v)}`;
  }).filter(Boolean).join('|');
  const text = tag === 'text' ? `|txt=${(el.textContent || '').trim()}` : '';
  return `${tag}|${attrs}${text}`;
};

const collectShapes = (svgEl) => {
  if (!svgEl) return [];
  const out = [];
  const walk = (node) => {
    if (node.nodeType !== 1) return;   // ELEMENT_NODE
    const tag = node.tagName.toLowerCase();
    if (SHAPE_TAGS.has(tag)) out.push(node);
    for (const child of node.children) walk(child);
  };
  for (const child of svgEl.children) walk(child);
  return out;
};

const getViewBox = (svgEl) => {
  const vb = svgEl?.getAttribute('viewBox');
  if (vb) return vb;
  const w = svgEl?.getAttribute('width') || '1200';
  const h = svgEl?.getAttribute('height') || '800';
  return `0 0 ${parseFloat(w) || 1200} ${parseFloat(h) || 800}`;
};

const cloneWithStyle = (el, style) => {
  const cp = el.cloneNode(true);
  cp.setAttribute('stroke', style.stroke);
  cp.setAttribute('fill', style.fill);
  cp.setAttribute('stroke-opacity', String(style.strokeOpacity));
  cp.setAttribute('stroke-width', el.getAttribute('stroke-width') || '2');
  // Text stays coloured but doesn't need a fill halo.
  if (el.tagName.toLowerCase() === 'text') {
    cp.setAttribute('fill', style.stroke);
    cp.removeAttribute('stroke');
  }
  return cp.outerHTML;
};

/**
 * diffSvgs(prev, next, { mode })
 *
 * Returns:
 *   {
 *     ok:     boolean,          // true if both SVGs parsed
 *     stats:  { added, removed, unchanged },
 *     overlay: string | null,   // single combined SVG overlay (mode='overlay')
 *     leftDiff:  string | null, // prev with removals highlighted (mode='split')
 *     rightDiff: string | null, // next with additions highlighted (mode='split')
 *   }
 */
export const diffSvgs = (prevRaw, nextRaw, { mode = 'overlay' } = {}) => {
  const prev = parseSvg(prevRaw);
  const next = parseSvg(nextRaw);
  if (!prev || !next) {
    return { ok: false, stats: { added: 0, removed: 0, unchanged: 0 }, overlay: null, leftDiff: null, rightDiff: null };
  }

  const prevShapes = collectShapes(prev);
  const nextShapes = collectShapes(next);
  const prevSigs = new Map();
  prevShapes.forEach((el) => prevSigs.set(signatureOf(el), el));
  const nextSigs = new Map();
  nextShapes.forEach((el) => nextSigs.set(signatureOf(el), el));

  const added = [];
  const removed = [];
  const unchanged = [];
  for (const [sig, el] of nextSigs) {
    if (prevSigs.has(sig)) unchanged.push({ prev: prevSigs.get(sig), next: el });
    else added.push(el);
  }
  for (const [sig, el] of prevSigs) {
    if (!nextSigs.has(sig)) removed.push(el);
  }

  const stats = { added: added.length, removed: removed.length, unchanged: unchanged.length };

  // Use `next`'s viewBox as the reference — it's the current version.
  const viewBox = getViewBox(next);
  const buildOverlay = (parts) => (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" ` +
    `preserveAspectRatio="xMidYMid meet" width="100%" height="100%" ` +
    `style="max-width:100%;max-height:100%;height:auto;width:100%;display:block">` +
    `<rect width="100%" height="100%" fill="#FFFFFF"/>` +
    parts.join('') +
    `</svg>`
  );

  if (mode === 'overlay') {
    const parts = [
      // Unchanged first (behind), then removed (red), then added (green).
      ...unchanged.map(({ next: el }) => cloneWithStyle(el, OVERLAY_STYLES.unchanged)),
      ...removed.map((el) => cloneWithStyle(el, OVERLAY_STYLES.removed)),
      ...added.map((el) => cloneWithStyle(el, OVERLAY_STYLES.added)),
    ];
    return { ok: true, stats, overlay: buildOverlay(parts), leftDiff: null, rightDiff: null };
  }

  // Split mode — draw prev with removals highlighted red, next with
  // additions highlighted green. Unchanged appears muted in both.
  const leftDiff = buildOverlay([
    ...unchanged.map(({ prev: el }) => cloneWithStyle(el, OVERLAY_STYLES.unchanged)),
    ...removed.map((el) => cloneWithStyle(el, OVERLAY_STYLES.removed)),
  ]);
  const rightDiff = buildOverlay([
    ...unchanged.map(({ next: el }) => cloneWithStyle(el, OVERLAY_STYLES.unchanged)),
    ...added.map((el) => cloneWithStyle(el, OVERLAY_STYLES.added)),
  ]);

  return { ok: true, stats, overlay: null, leftDiff, rightDiff };
};
